import { useCallback, useMemo } from 'react'

import { useRouter } from 'next/router'

import type { BreadcrumbItem } from '@/components/common/Breadcrumb'
import { HomeBreadcrumb } from '@/components/library-v2/ui/Layout'

import {
  useEntitySchema,
  useEntitySchemas,
} from '@/hooks/library/useEntitySchemas'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { EntityExtendedJsonSchema } from '@/types/library/ExtendedSchema'

interface UseCatalogEntrySchemaArgs {
  schemaId: UUID | ''
}

export const useCatalogEntrySchema = ({
  schemaId,
}: UseCatalogEntrySchemaArgs) => {
  const { activeOrgId } = useActiveOrgId()
  const selectCatalogSchema = useCallback(
    (data: PaginatedResponse<EntityExtendedJsonSchema>) => {
      return data.data.find((s) => s.id === schemaId)
    },
    [schemaId],
  )
  return useEntitySchemas({
    orgId: activeOrgId,
    select: selectCatalogSchema,
  })
}

interface UseCatalogEntryFieldArgs {
  schemaId: UUID | ''
  fieldId: string
}

export const useCatalogEntryField = ({
  schemaId,
  fieldId,
}: UseCatalogEntryFieldArgs) => {
  const { activeOrgId } = useActiveOrgId()

  const selectCatalogSchemaField = useCallback(
    (data: PaginatedResponse<EntityExtendedJsonSchema>) => {
      const schema = data.data.find((s) => s.id === schemaId)
      const field = schema?.fields.find((f) => f.id === fieldId)
      return field
    },
    [schemaId, fieldId],
  )
  return useEntitySchemas({
    orgId: activeOrgId,
    select: selectCatalogSchemaField,
  })
}

interface UseCatalogSchemaBreadcrumbsArgs {
  schemaId: UUID
}

export const useCatalogSchemaBreadcrumbs = ({
  schemaId,
}: UseCatalogSchemaBreadcrumbsArgs) => {
  const { activeOrgId } = useActiveOrgId()

  const { data: schema } = useEntitySchema({
    orgId: activeOrgId,
    schemaId: schemaId,
  })
  2
  return useMemo(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { children: <HomeBreadcrumb />, href: '/' },
      { children: 'Catalog', href: '/catalog' },
    ]
    if (schema) {
      breadcrumbs.push({
        children: schema.name,
        href: `/catalog?schema-id=${schema.id}`,
      })
    }
    return breadcrumbs
  }, [schema?.id, schema?.name])
}

export const useActiveCatalogSchemaId = () => {
  const router = useRouter()
  const schemaId = router.query['schema-id']
  return {
    activeCatalogSchemaId: (typeof schemaId === 'string' ? schemaId : '') as
      | UUID
      | '',
  }
}
